// src/components/ExportDialog.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Autocomplete, Box, IconButton,
  Grid, Typography, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Paper, TableFooter, Divider,
  Checkbox, FormControlLabel, Select, MenuItem, InputLabel, FormControl
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';

/* =========================
   Helpers
========================= */
const generateSoNumber = () =>
  `SO${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '')}`;

const formatCurrency = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '0 ₫';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(Number(number));
};

/* =========================
   Component
========================= */
function ExportDialog({
  open,
  onClose,
  onSave,
  products,
  customers,
  drivers,
  assistantDrivers,
  vehicles,
  warehouses,
  onAddNew,
  initialExportOrder
}) {
  // Header State
  const [soDate, setSoDate] = useState(new Date());
  const [shippingDate, setShippingDate] = useState(new Date());
  const [soNumber, setSoNumber] = useState('');
  const [status, setStatus] = useState('processing');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedAssistantDriver, setSelectedAssistantDriver] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [note, setNote] = useState('');

  // Financial State
  const [shippingFee, setShippingFee] = useState(0);
  const [taxRate, setTaxRate] = useState(10);
  const [amountPaid, setAmountPaid] = useState(0);

  // Line Items State
  const [items, setItems] = useState([
    { product: null, quantity: 1, exportPrice: 0, isCanExchange: false, isCanBorrow: false }
  ]);

  // Derived Calculations
  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.quantity || 0) * Number(item.exportPrice || 0)), 0),
    [items]
  );
  const taxAmount = useMemo(() => subtotal * (taxRate / 100), [subtotal, taxRate]);
  const grandTotal = useMemo(() => subtotal + taxAmount + Number(shippingFee || 0), [subtotal, taxAmount, shippingFee]);
  const remainingAmount = useMemo(() => grandTotal - Number(amountPaid || 0), [grandTotal, amountPaid]);

  useEffect(() => {
    if (!open) return;

    if (initialExportOrder) {
      // EDIT MODE
      setSoNumber(initialExportOrder.soNumber || generateSoNumber());
      setSoDate(initialExportOrder.soDate?.toDate?.() || initialExportOrder.soDate || new Date());
      setShippingDate(initialExportOrder.shippingDate?.toDate?.() || initialExportOrder.shippingDate || new Date());
      setStatus(initialExportOrder.status || 'processing');

      setSelectedCustomer(customers.find(c => c.id === initialExportOrder.customerId) || null);
      setSelectedDriver(drivers.find(d => d.id === initialExportOrder.driverId) || null);
      setSelectedAssistantDriver(assistantDrivers.find(a => a.id === initialExportOrder.assistantDriverId) || null);
      setSelectedVehicle(vehicles.find(v => v.id === initialExportOrder.vehicleId) || null);
      setSelectedWarehouse(warehouses.find(w => w.id === initialExportOrder.warehouseId) || null);

      setNote(initialExportOrder.note || '');
      setShippingFee(initialExportOrder.shippingFee || 0);
      setTaxRate(initialExportOrder.taxRate !== undefined ? initialExportOrder.taxRate : 10);
      setAmountPaid(initialExportOrder.amountPaid || 0);

      const itemsWithData = (initialExportOrder.items || []).map(item => ({
        ...item,
        product: products.find(p => p.id === item.productId) || null,
        isCanExchange: !!item.isCanExchange,
        isCanBorrow: !!item.isCanBorrow
      }));
      setItems(itemsWithData.length > 0
        ? itemsWithData
        : [{ product: null, quantity: 1, exportPrice: 0, isCanExchange: false, isCanBorrow: false }]);
    } else {
      // CREATE MODE
      setSoNumber(generateSoNumber());
      setSoDate(new Date());
      setShippingDate(new Date());
      setStatus('processing');
      setSelectedCustomer(null);
      setSelectedDriver(null);
      setSelectedAssistantDriver(null);
      setSelectedVehicle(null);
      setSelectedWarehouse(null);
      setNote('');
      setShippingFee(0);
      setTaxRate(10);
      setAmountPaid(0);
      setItems([{ product: null, quantity: 1, exportPrice: 0, isCanExchange: false, isCanBorrow: false }]);
    }
  }, [open, initialExportOrder, products, customers, drivers, assistantDrivers, vehicles, warehouses]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    if (field === 'isCanExchange' || field === 'isCanBorrow') {
      newItems[index][field] = value.target.checked;
    } else {
      newItems[index][field] = value;
    }
    if (field === 'product' && value) {
      newItems[index].exportPrice = value.price || 0;
    }
    setItems(newItems);
  };

  const handleAddItem = () =>
    setItems([...items, { product: null, quantity: 1, exportPrice: 0, isCanExchange: false, isCanBorrow: false }]);

  const handleRemoveItem = (index) =>
    setItems(items.filter((_, i) => i !== index));

  const handleSave = () => {
    if (!selectedCustomer || items.some(item => !item.product || !item.quantity || Number(item.quantity) <= 0)) {
      alert('Vui lòng nhập Tên khách hàng và đảm bảo các dòng sản phẩm hợp lệ.');
      return;
    }
    const exportData = {
      soNumber,
      soDate,
      shippingDate,
      note,
      status,
      customerId: selectedCustomer?.id || null,
      customerName: selectedCustomer?.name || null,
      driverId: selectedDriver?.id || null,
      driverName: selectedDriver?.name || null,
      assistantDriverId: selectedAssistantDriver?.id || null,
      assistantDriverName: selectedAssistantDriver?.name || null,
      vehicleId: selectedVehicle?.id || null,
      vehiclePlate: selectedVehicle?.name || null,
      warehouseId: selectedWarehouse?.id || null,
      warehouseName: selectedWarehouse?.name || null,
      subtotal,
      taxRate,
      taxAmount,
      shippingFee: Number(shippingFee || 0),
      grandTotal,
      amountPaid: Number(amountPaid || 0),
      remainingAmount,
      items: items.map(item => ({
        productId: item.product.id,
        productName: item.product.name,
        productSku: item.product.sku,
        quantity: Number(item.quantity),
        exportPrice: Number(item.exportPrice),
        isCanExchange: item.isCanExchange,
        isCanBorrow: item.isCanBorrow
      }))
    };
    onSave(exportData);
  };

  /* -------- Autocomplete helper.
     Thêm tham số `big` để làm ô to hơn (cao + chữ lớn + minWidth) -------- */
  const renderAutocomplete = (options, value, onChange, label, onAdd, big = false) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Autocomplete
        fullWidth
        autoHighlight
        options={options}
        value={value}
        onChange={(_, val) => onChange(val)}
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={(option, val) => option?.id === val?.id}
        sx={{
          flexGrow: 1,
          ...(big
            ? {
                minWidth: 560,
                '& .MuiOutlinedInput-root': { height: 56 },
                '& .MuiInputBase-input': { fontSize: 16 },
                '& .MuiInputLabel-root': { fontSize: 16 }
              }
            : {})
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            label={label}
            variant="outlined"
            size={big ? 'medium' : 'small'}
          />
        )}
      />
      <IconButton color="primary" onClick={onAdd} title={`Thêm ${label} mới`}>
        <AddCircleOutlineIcon />
      </IconButton>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>
        {initialExportOrder ? `Chỉnh sửa phiếu xuất ${initialExportOrder.soNumber}` : 'Tạo Phiếu Xuất Hàng Mới'}
      </DialogTitle>

      <DialogContent>
        {/* ### UPDATED LAYOUT FOR WIDER FIELDS ### */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Row 1: Customer (Full Width, BIG) */}
          <Grid item xs={12}>
            {renderAutocomplete(
              customers,
              selectedCustomer,
              setSelectedCustomer,
              'Khách hàng (*)',
              () => onAddNew('customer'),
              true // <— làm ô Khách hàng TO hơn (cao + chữ lớn)
            )}
          </Grid>

          {/* Row 2: Warehouse (Full Width) */}
          <Grid item xs={12}>
            {renderAutocomplete(
              warehouses,
              selectedWarehouse,
              setSelectedWarehouse,
              'Xuất tại kho',
              () => onAddNew('warehouse')
            )}
          </Grid>

          {/* Row 3: Order Info */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Số SO (tự động)" value={soNumber} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              label="Ngày lập SO"
              value={soDate}
              onChange={setSoDate}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              label="Ngày xuất hàng"
              value={shippingDate}
              onChange={setShippingDate}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          {/* Row 4: Status and Note */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Trạng thái đơn hàng</InputLabel>
              <Select
                value={status}
                label="Trạng thái đơn hàng"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="processing">Đang xử lý</MenuItem>
                <MenuItem value="shipped">Đã chuyển</MenuItem>
                <MenuItem value="cancelled">Hàng hủy</MenuItem>
                <MenuItem value="other">Khác</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField label="Ghi chú" fullWidth value={note} onChange={(e) => setNote(e.target.value)} />
          </Grid>

          {/* Shipping Info Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ pt: 1, fontWeight: 'bold' }}>
              Thông tin vận chuyển
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {renderAutocomplete(drivers, selectedDriver, setSelectedDriver, 'Tài xế', () => onAddNew('driver'))}
          </Grid>
          <Grid item xs={12}>
            {renderAutocomplete(
              assistantDrivers,
              selectedAssistantDriver,
              setSelectedAssistantDriver,
              'Phụ xe',
              () => onAddNew('assistantDriver')
            )}
          </Grid>
          <Grid item xs={12}>
            {renderAutocomplete(
              vehicles,
              selectedVehicle,
              setSelectedVehicle,
              'Biển số xe',
              () => onAddNew('vehicle')
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Chi tiết sản phẩm
        </Typography>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '30%' }}>Sản phẩm (*)</TableCell>
                <TableCell sx={{ width: '10%' }}>Số lượng (*)</TableCell>
                <TableCell sx={{ width: '15%' }}>Giá xuất</TableCell>
                <TableCell sx={{ width: '25%' }}>Loại</TableCell>
                <TableCell sx={{ width: '15%' }} align="right">Thành tiền</TableCell>
                <TableCell align="center">Xóa</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Autocomplete
                      options={products}
                      value={item.product}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      getOptionLabel={(option) => `${option.name} (Tồn: ${option.quantity})`}
                      onChange={(e, val) => handleItemChange(index, 'product', val)}
                      renderInput={(params) => (
                        <TextField {...params} variant="standard" placeholder="Tìm sản phẩm..." />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      variant="standard"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={item.exportPrice}
                      onChange={(e) => handleItemChange(index, 'exportPrice', e.target.value)}
                      variant="standard"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={item.isCanExchange || false}
                          onChange={(e) => handleItemChange(index, 'isCanExchange', e)}
                          size="small"
                        />
                      }
                      label="Bán vỏ"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={item.isCanBorrow || false}
                          onChange={(e) => handleItemChange(index, 'isCanBorrow', e)}
                          size="small"
                        />
                      }
                      label="Mượn vỏ"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(Number(item.quantity || 0) * Number(item.exportPrice || 0))}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleRemoveItem(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Button startIcon={<AddCircleOutlineIcon />} onClick={handleAddItem} sx={{ mt: 1 }}>
          Thêm dòng
        </Button>

        <Divider sx={{ my: 2 }} />

        <Grid container justifyContent="flex-end" spacing={1}>
          <Grid item xs={6} sm={4} md={3}>
            <Typography align="right">Tiền hàng trước thuế:</Typography>
          </Grid>
          <Grid item xs={6} sm={2} md={2}>
            <Typography align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(subtotal)}</Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Thuế GTGT (%)"
              type="number"
              size="small"
              value={taxRate}
              onChange={e => setTaxRate(Number(e.target.value))}
            />
          </Grid>
          <Grid item xs={6} sm={2} md={2}>
            <Typography align="right">{formatCurrency(taxAmount)}</Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Cước vận chuyển"
              type="number"
              size="small"
              value={shippingFee}
              onChange={e => setShippingFee(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={2} md={2}>
            <Typography align="right">{formatCurrency(Number(shippingFee || 0))}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider variant="middle" sx={{ my: 1 }} />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <Typography align="right" variant="h6">Tổng cộng:</Typography>
          </Grid>
          <Grid item xs={6} sm={2} md={2}>
            <Typography align="right" variant="h6" color="primary">{formatCurrency(grandTotal)}</Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Số tiền đã trả"
              type="number"
              size="small"
              variant="filled"
              value={amountPaid}
              onChange={e => setAmountPaid(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={2} md={2}>
            <Typography align="right" color="green">{formatCurrency(Number(amountPaid || 0))}</Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <Typography align="right" variant="h6">Còn lại:</Typography>
          </Grid>
          <Grid item xs={6} sm={2} md={2}>
            <Typography align="right" variant="h6" color="error">{formatCurrency(remainingAmount)}</Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Hủy</Button>
        <Button onClick={handleSave} variant="contained">Lưu Phiếu Xuất</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExportDialog;
